/**
 * Home — public landing inside AppShell. Anonymous visitors get a static
 * Trending teaser (GetFeeds requires auth); signed-in members get live
 * Trending / WLB / Buddies feeds via FeedContext, same as mobile.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import { useAuth } from '@wlb/shared/context/AuthContext';
import { useFeed } from '@wlb/shared/context/FeedContext';
import { SKINNY_NEWS_URL } from '@wlb/shared/config/env';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { FeedTab } from '@wlb/shared/types/feed';
import type { FeedPost } from '@wlb/shared/types/post';
import profileService from '@wlb/shared/api/services/profileService';
import feedService from '@wlb/shared/api/services/feedService';
import { ToastService } from '@wlb/shared/platform/notifier';
import { STORAGE_KEYS } from '@wlb/shared/constants';
import { storage } from '@wlb/shared/platform/storage';
import {
  BuddiesTabIcon,
  FilterIcon,
  GroupsTabIcon,
  TrendingIcon,
  WlbFeedIcon,
} from '../components/ui/icons';
import skinnyNewsIcon from '../assets/images/skinnynews.png';
import { InfiniteScrollFooter } from '../components/ui/InfiniteScrollFooter';
import { ListStatus } from '../components/ui/ListStatus';
import { useAuthGate } from '../components/feedback/AuthGate';
import { useCurrentProfile } from '../features/profile/useCurrentProfile';
import { useOpenUserProfile } from '../features/profile/useOpenUserProfile';
import { useOpenDirectConversation } from '../features/messages/useOpenDirectConversation';
import { PostCard } from '../features/feed/PostCard';
import { FeedPostComposer } from '../features/feed/FeedPostComposer';
import { PostListSkeleton } from '../features/feed/PostListSkeleton';
import { STATIC_TRENDING_POSTS } from '../features/feed/staticPosts';
import { CommentsModal } from '../features/feed/CommentsModal';
import { LikesModal } from '../features/feed/LikesModal';
import { FilterPostsModal } from '../features/feed/FilterPostsModal';
import { PostOptionsModal } from '../features/feed/PostOptionsModal';
import type { PostOptionKey } from '../features/feed/postOptions';
import { ReportPostModal } from '../features/feed/ReportPostModal';
import { SharePostModal } from '../features/feed/SharePostModal';
import { ConfirmModal } from '../features/feed/ConfirmModal';
import { BlockUserModal } from '../features/feed/BlockUserModal';
import { RobiInfoModal } from '../features/feed/RobiInfoModal';
import { usePostBuddySuggest } from '../features/feed/usePostBuddySuggest';
import { useTrendingRevalidation } from '../features/feed/useTrendingRevalidation';
import { resolvePostImageUri } from '../features/feed/postMedia';
import {
  editPostRouteState,
  newPostRouteState,
  shouldShowFirstPostTutorial,
  type CreatePostIntent,
} from '../features/createPost/createPostRoute';
import type { MediaFile } from '@wlb/shared/types/post';
import { useRestoreScrollOnReturn } from '../features/createPost/useRestoreScrollOnReturn';
import { formatHashtag, parseHashtagFilter, postMatchesAnyHashtag } from '../utils/hashtags';
import { getPageScrollY } from '../utils/pageScrollLock';
import { RobiQuickAnswerModal } from '../features/robi/RobiQuickAnswerModal';
import { RobiQuickAnswerDisclaimerModal } from '../features/robi/RobiQuickAnswerDisclaimerModal';
import { AiBuddyDisclaimerModal } from '../features/settings/AiBuddyDisclaimerModal';
import { SecuredByAiLink } from '../components/layout/SecuredByAiLink';
import { FirstPostTutorialCard } from '../features/support/FirstPostTutorialCard';
import { GroupTabs, type GroupTabKey } from '../features/groups/GroupTabs';
import { GroupsPanel } from '../features/groups/GroupsPanel';
import { readGroupsTab, writeGroupsTab } from '../features/groups/groupsViewState';
import '../components/ui/skeleton.css';
import '../features/feed/feed.css';

const TABS: { key: FeedTab; labelKey: string; icon: ComponentType<{ size?: number; className?: string }> }[] = [
  { key: 'trending', labelKey: 'home.topTabs.trending', icon: TrendingIcon },
  { key: 'wlb', labelKey: 'home.topTabs.wlb', icon: WlbFeedIcon },
  { key: 'buddies', labelKey: 'home.topTabs.buddies', icon: BuddiesTabIcon },
  { key: 'groups', labelKey: 'home.topTabs.groups', icon: GroupsTabIcon },
];

const SKINNY_NEWS_FALLBACK_URL = 'https://skinnynews.com';

export function Home() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { requireAuth } = useAuthGate();
  const navigate = useNavigate();
  const location = useLocation();
  const openUserProfile = useOpenUserProfile();
  const { openConversation } = useOpenDirectConversation();
  const profile = useCurrentProfile();
  const {
    activeTab: memberTab,
    posts,
    loading,
    error,
    loaded,
    loadMoreError,
    isFetchingMore,
    isRefreshing,
    hasNextPage,
    setActiveTab,
    fetchNextPage,
    refreshFeed,
    retryFeed,
    requestFeedRefresh,
    likePost,
    savePost,
    deletePost,
    toggleBlockUser,
    updateCommentCount,
    searchHashtag,
    searchQuery,
  } = useFeed();
  const [searchParams, setSearchParams] = useSearchParams();
  const tagParam = searchParams.get('tag');
  const postIdParam = searchParams.get('postId');

  // When arriving at /?tag=Progress (e.g. from a hashtag click), apply the
  // hashtag filter so the feed shows only matching posts.
  useEffect(() => {
    if (!isAuthenticated || !tagParam) return;
    const tag = formatHashtag(tagParam);
    if (!tag) return;
    searchHashtag(tag).catch(() => undefined);
  }, [tagParam, isAuthenticated, searchHashtag]);

  /**
   * Tags currently filtering the feed, from the URL or the applied search.
   * The Filter Posts sheet can apply several at once; a plain text search is
   * not a tag filter, so it is left alone.
   */
  const activeHashtags = useMemo(() => {
    const fromSearch = searchQuery?.trim().startsWith('#') ? searchQuery.trim() : '';
    return parseHashtagFilter(fromSearch || tagParam || '');
  }, [searchQuery, tagParam]);

  const handleClearHashtag = () => {
    setSearchParams({}, { replace: true });
    searchHashtag(null).catch(() => undefined);
  };

  const openCreatePost = (
    intent: CreatePostIntent,
    initialMedia?: MediaFile,
  ) => {
    navigate('/create', {
      state: newPostRouteState(intent, location, initialMedia),
    });
  };

  const [commentPost, setCommentPost] = useState<FeedPost | null>(null);

  useEffect(() => {
    if (
      !isAuthenticated
      || !postIdParam
    ) {
      return;
    }

    let active = true;

    feedService.getPostDetails(postIdParam).then(result => {
      if (!active) return;

      const nextParams = new URLSearchParams(location.search);
      nextParams.delete('postId');
      setSearchParams(nextParams, { replace: true });

      if (result.success && result.data?.post) {
        setCommentPost(result.data.post);
        return;
      }

      ToastService.show({
        type: 'error',
        message: t('common.errors.generic'),
      });
    });

    return () => {
      active = false;
    };
  }, [
    isAuthenticated,
    location.search,
    postIdParam,
    setSearchParams,
    t,
  ]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [likesPost, setLikesPost] = useState<FeedPost | null>(null);
  const [mediaPost, setMediaPost] = useState<FeedPost | null>(null);
  const [mediaIndex, setMediaIndex] = useState(0);
  // Stable so PostCard's memo holds: an unstable handler would re-render every
  // card already in the feed each time a page is appended.
  const handleMediaClick = useCallback((post: FeedPost, index?: number) => {
    setMediaPost(post);
    setMediaIndex(index ?? 0);
  }, []);
  const [menuPost, setMenuPost] = useState<FeedPost | null>(null);
  const [menuAnchorRect, setMenuAnchorRect] = useState<DOMRect | null>(null);
  const [reportPost, setReportPost] = useState<FeedPost | null>(null);
  const [sharePostTarget, setSharePostTarget] = useState<FeedPost | null>(null);
  const [deleteTargetPost, setDeleteTargetPost] = useState<FeedPost | null>(null);
  const [blockTargetPost, setBlockTargetPost] = useState<FeedPost | null>(null);
  const [blockLoading, setBlockLoading] = useState(false);
  const [robiDisclaimerOpen, setRobiDisclaimerOpen] = useState(false);
  const [robiChatOpen, setRobiChatOpen] = useState(false);
  const postBuddy = usePostBuddySuggest();
  // Restored from the session so returning from a group or profile lands on the
  // sub-tab the member left, not back on Posts.
  const [groupTab, setGroupTab] = useState<GroupTabKey>(readGroupsTab);
  const scrollPositions = useRef<Partial<Record<string, number>>>({});
  const activeTabRef = useRef<FeedTab>(isAuthenticated ? memberTab : 'trending');

  // Guests only get the static Trending teaser; members land on their WLB feed.
  const activeTab: FeedTab = isAuthenticated ? memberTab : 'trending';
  const [showFirstPostTutorial, setShowFirstPostTutorial] = useState(
    () => shouldShowFirstPostTutorial(location.state),
  );

  // Trending is a live ranking, so a cached snapshot is only a starting point.
  useTrendingRevalidation(activeTab, isAuthenticated);

  /**
   * A hashtag filter must match whole tags: a post tagged #Fitness is not a
   * result for #Fit. The API matches loosely, so the tags are re-checked here.
   */
  const visiblePosts = useMemo(
    () =>
      activeHashtags.length
        ? posts.filter(post => postMatchesAnyHashtag(post, activeHashtags))
        : posts,
    [posts, activeHashtags],
  );

  // Returning from an edit lands on the post the user was looking at. The list
  // has to be rendered first, or the offset is clamped to the skeleton height.
  useRestoreScrollOnReturn(visiblePosts.length > 0);

  useEffect(() => {
    if (!shouldShowFirstPostTutorial(location.state)) return;
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    const handleScroll = () => {
      // An open overlay pins the body, which fires a scroll event reading 0;
      // recording that would lose the tab's position.
      scrollPositions.current[activeTabRef.current] = getPageScrollY();
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    activeTabRef.current = activeTab;
    // Groups load asynchronously and restore their own offset once rendered.
    if (activeTab === 'groups') return;
    const position = scrollPositions.current[activeTab] ?? 0;
    window.requestAnimationFrame(() => window.scrollTo({ top: position, behavior: 'auto' }));
  }, [activeTab]);

  const handleGroupTabSelect = (tab: GroupTabKey) => {
    setGroupTab(tab);
    writeGroupsTab(tab);
  };

  const handleTabSelect = (tab: FeedTab) => {
    if (!isAuthenticated) {
      // Never call FeedContext anonymously — GetFeeds 401s and would raise the
      // session-expired dialog on the public landing page.
      if (tab !== 'trending') {
        requireAuth();
      }
      return;
    }
    scrollPositions.current[activeTab] = getPageScrollY();
    activeTabRef.current = tab;
    setActiveTab(tab);
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: scrollPositions.current[tab] ?? 0, behavior: 'auto' });
    });
  };

  const openSkinnyNews = () => {
    const url = SKINNY_NEWS_URL || SKINNY_NEWS_FALLBACK_URL;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Mobile gates the first Quick Answer chat behind a disclaimer the user can
  // opt out of; the acceptance flag is shared across platforms.
  const openRobiQuickAnswer = useCallback(async () => {
    const accepted = await storage.getItem<boolean>(
      STORAGE_KEYS.DONT_ASK_ROBI_QUICK_ANSWER_SHEET,
      false,
    );
    if (accepted === true) {
      setRobiChatOpen(true);
      return;
    }
    setRobiDisclaimerOpen(true);
  }, []);

  const robiQuickAnswerRequested = searchParams.get('robi') === 'quick-answer';

  useEffect(() => {
    if (!robiQuickAnswerRequested) return;

    setSearchParams(current => {
      const next = new URLSearchParams(current);
      next.delete('robi');
      return next;
    }, { replace: true });
    requireAuth(() => {
      openRobiQuickAnswer().catch(() => undefined);
    });
  }, [
    openRobiQuickAnswer,
    requireAuth,
    robiQuickAnswerRequested,
    setSearchParams,
  ]);

  const handleRobiDisclaimerContinue = (dontShowAgain: boolean) => {
    setRobiDisclaimerOpen(false);
    setRobiChatOpen(true);
    if (dontShowAgain) {
      storage
        .setItem(STORAGE_KEYS.DONT_ASK_ROBI_QUICK_ANSWER_SHEET, true)
        .catch(() => undefined);
    }
  };

  const handleMenuOpen = useCallback((post: FeedPost, anchorRect: DOMRect) => {
    setMenuPost(post);
    setMenuAnchorRect(anchorRect);
  }, []);

  const handleMenuClose = () => {
    setMenuPost(null);
    setMenuAnchorRect(null);
  };

  const handleMenuSelect = async (action: PostOptionKey) => {
    if (!menuPost) return;
    const currentPost = menuPost;
    setMenuPost(null);
    setMenuAnchorRect(null);

    switch (action) {
      case 'save': {
        const success = await savePost(currentPost.id);
        if (success) requestFeedRefresh();
        break;
      }
      case 'message':
        // Resolves by numeric user id, so the existing thread with this member
        // is reused and only a first-time chat hits Conversation/Initiate.
        // The feed is public, so an anonymous reader signs in first.
        requireAuth(() => {
          openConversation({
            userId: currentPost.userId ?? 0,
            name: currentPost.username,
            avatar: typeof currentPost.avatar === 'string' ? currentPost.avatar : null,
          }).catch(() => undefined);
        });
        break;
      case 'profile':
        // Only the GUID resolves a profile; a numeric userId dead-ends on the
        // "unavailable" screen. The hook also routes own posts to /profile.
        openUserProfile(currentPost.profileUserGuid);
        break;
      case 'report':
        setReportPost(currentPost);
        break;
      case 'block':
        setBlockTargetPost(currentPost);
        break;
      case 'edit':
        // The offset travels with the edit so the save lands back on this post
        // instead of the top of the feed. The options menu is still pinning the
        // body here, so the offset has to come from the lock.
        navigate('/create', {
          state: editPostRouteState(currentPost, location, getPageScrollY()),
        });
        break;
      case 'delete':
        setDeleteTargetPost(currentPost);
        break;
      default:
        break;
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetPost) return;
    const target = deleteTargetPost;
    setDeleteTargetPost(null);
    const success = await deletePost(target.id);
    if (success) requestFeedRefresh();
  };

  const handleConfirmBlock = async () => {
    if (!blockTargetPost) return;
    const targetUserId = blockTargetPost.userId;
    const username = blockTargetPost.username;
    if (targetUserId == null) {
      ToastService.show({ type: 'error', message: t('common.errors.generic') });
      return;
    }
    setBlockLoading(true);
    const shouldBlock = !blockTargetPost.isBlocked;
    const result = await profileService.setBlockRelation(targetUserId, shouldBlock);
    setBlockLoading(false);
    if (result.success) {
      toggleBlockUser(targetUserId, username, shouldBlock);
      setBlockTargetPost(null);
      requestFeedRefresh();
      const successMessage = shouldBlock 
        ? t('home.postOptions.blockSuccessMessage', { username })
        : t('home.postOptions.unblockSuccessMessage', { username });
      ToastService.show({ type: 'success', message: successMessage });
      return;
    }
    ToastService.show({ type: 'error', message: result.message || t('common.errors.generic') });
  };

  const renderFeedContent = () => {
    if (!isAuthenticated) {
      return (
        <div className="feed-list">
          {STATIC_TRENDING_POSTS.map(post => (
            <PostCard key={post.id} post={post} onComment={setCommentPost} onShare={setSharePostTarget} onLikes={setLikesPost} onMediaClick={handleMediaClick} onMenu={handleMenuOpen} />
          ))}
        </div>
      );
    }

    // Groups run on GroupContext, not FeedContext, so they own their content.
    if (activeTab === 'groups') {
      return <GroupsPanel activeTab={groupTab} />;
    }

    if (loading && visiblePosts.length === 0) {
      return <PostListSkeleton label={t('home.feed.loading')} />;
    }

    // A failed load is reported as a failure, never as an empty feed.
    if (error && posts.length === 0) {
      return (
        <ListStatus
          variant="error"
          message={error}
          onAction={retryFeed}
          busy={loading || isRefreshing}
        />
      );
    }

    // Nothing to show yet and nothing answered: keep the loader rather than
    // claiming the tab is empty.
    if (!loaded && visiblePosts.length === 0) {
      return <PostListSkeleton label={t('home.feed.loading')} />;
    }

    if (visiblePosts.length === 0) {
      // Filter results: re-running the same query cannot produce posts, so no
      // refresh is offered — the member has to change the filter.
      if (activeHashtags.length) {
        return (
          <ListStatus
            variant="empty"
            title={t('home.feed.noPosts')}
            message={t('home.feed.noHashtagMatches')}
          />
        );
      }
      if (activeTab === 'buddies') {
        return (
          <ListStatus
            variant="empty"
            title={t('home.emptyStates.buddies.title')}
            message={t('home.emptyStates.buddies.subtitle')}
            actionLabel={t('home.emptyStates.buddies.button')}
            onAction={() => navigate('/buddies')}
          />
        );
      }
      return (
        <ListStatus
          variant="empty"
          title={t('home.feed.noPosts')}
          message={t('home.feed.noPostsSubtitle')}
          onAction={refreshFeed}
          busy={isRefreshing}
        />
      );
    }

    return (
      <>
        <div className="feed-list skeleton-fade-in" key={activeTab}>
          {visiblePosts.map(post => (
            <PostCard
              key={post.id}
              post={post}
              onLike={likePost}
              onComment={setCommentPost}
              onShare={setSharePostTarget}
              onLikes={setLikesPost}
              onMediaClick={handleMediaClick}
              onMenu={handleMenuOpen}
              onRobiPress={activeTab === 'wlb' ? postBuddy.handleRobiPress : undefined}
              robiLoadingPostId={postBuddy.robiLoadingPostId}
            />
          ))}
        </div>
        {loadMoreError ? (
          <ListStatus
            variant="error"
            layout="inline"
            message={loadMoreError}
            onAction={fetchNextPage}
            busy={isFetchingMore}
          />
        ) : (
          /* Counted on the unfiltered list: a page whose posts all fail the
             hashtag re-check still made progress, so the next one should run. */
          <InfiniteScrollFooter
            hasNextPage={hasNextPage}
            isFetchingMore={isFetchingMore}
            onLoadMore={fetchNextPage}
            itemCount={posts.length}
          />
        )}
      </>
    );
  };

  return (
    <section aria-label={t('home.tabs.home')}>
      <div className="feed-header">
        <div className="feed-tabs" role="tablist" aria-label={t('home.tabs.home')}>
          {TABS.map(tab => {
            const isActive = activeTab === tab.key;
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.key}
                role="tab"
                aria-selected={isActive}
                className={`feed-tabs__tab${isActive ? ' feed-tabs__tab--active' : ''}`}
                onClick={() => handleTabSelect(tab.key)}
              >
                <TabIcon size={18} className="feed-tabs__tab-icon" />
                <span>{t(tab.labelKey)}</span>
              </button>
            );
          })}
        </div>
        {activeTab === 'groups' && isAuthenticated && (
          <GroupTabs
            activeTab={groupTab}
            onTabChange={handleGroupTabSelect}
            onCreateGroup={() => navigate('/groups/create')}
          />
        )}
        {activeTab === 'wlb' && <div className="feed-header__tools">
          <SecuredByAiLink variant="compact" />
          <button
            className="feed-header__skinny-news"
            type="button"
            onClick={() => requireAuth(openSkinnyNews)}
            aria-label={t('home.filterHeader.skinnyNews')}
          >
            <img className="feed-header__skinny-news-image" src={skinnyNewsIcon} alt="" aria-hidden />
          </button>
          {isAuthenticated && (
            <FeedPostComposer
              profile={profile}
              onOpenComposer={openCreatePost}
            />
          )}
          <button className="feed-header__filter" type="button" onClick={() => requireAuth(() => setFilterOpen(true))} aria-label={t('home.accessibility.filterFeed')}><FilterIcon size={16} /></button>
        </div>}
      </div>

      <div role="tabpanel">
        {showFirstPostTutorial && (
          <FirstPostTutorialCard
            onDismiss={() => setShowFirstPostTutorial(false)}
          />
        )}
        {isAuthenticated && searchQuery && (
          <div className="feed-hashtag-banner">
            <span className="feed-hashtag-banner__text">
              {t('home.feed.showingPostsTaggedWith')}{' '}
              <span className="feed-hashtag-banner__tag">{searchQuery}</span>
            </span>
            <button
              type="button"
              className="feed-hashtag-banner__clear"
              onClick={handleClearHashtag}
            >
              {t('home.feed.clearHashtagFilter')}
            </button>
          </div>
        )}
        {renderFeedContent()}
      </div>
      {commentPost && <CommentsModal post={commentPost} initialView="comments" onClose={() => setCommentPost(null)} onCommentAdded={() => updateCommentCount(commentPost.id, 1)} onLikePost={likePost} onCommentCountChange={delta => updateCommentCount(commentPost.id, delta)} onCommentCountResolved={total => { const current = posts.find(item => item.id === commentPost.id)?.comments; if (current != null && current !== total) updateCommentCount(commentPost.id, total - current); }} onMenu={handleMenuOpen} />}
      {filterOpen && <FilterPostsModal onClose={() => setFilterOpen(false)} />}
      {likesPost && <LikesModal postId={likesPost.id} onClose={() => setLikesPost(null)} />}
      {mediaPost && <CommentsModal post={mediaPost} fullView mediaViewer initialMediaIndex={mediaIndex} onClose={() => setMediaPost(null)} onCommentAdded={() => updateCommentCount(mediaPost.id, 1)} onLikePost={likePost} onCommentCountChange={delta => updateCommentCount(mediaPost.id, delta)} onCommentCountResolved={total => { const current = posts.find(item => item.id === mediaPost.id)?.comments; if (current != null && current !== total) updateCommentCount(mediaPost.id, total - current); }} onMenu={handleMenuOpen} />}
      <PostOptionsModal post={menuPost} profile={profile} anchorRect={menuAnchorRect} onClose={handleMenuClose} onSelect={handleMenuSelect} />
      {reportPost && (
        <ReportPostModal
          post={reportPost}
          onClose={() => setReportPost(null)}
          onReported={requestFeedRefresh}
        />
      )}
      {sharePostTarget && (
        <SharePostModal post={sharePostTarget} onClose={() => setSharePostTarget(null)} />
      )}
      {deleteTargetPost && (
        <ConfirmModal
          title={t('home.postOptions.deleteConfirmTitle')}
          message={t('home.postOptions.deleteConfirmMessage')}
          confirmLabel={t('home.postOptions.deleteConfirm')}
          cancelLabel={t('home.postOptions.deleteConfirmCancel')}
          mediaUrl={resolvePostImageUri(deleteTargetPost.image)}
          onClose={() => setDeleteTargetPost(null)}
          onConfirm={() => { handleConfirmDelete().catch(() => undefined); }}
          showIcon
        />
      )}
      {robiDisclaimerOpen && (
        <RobiQuickAnswerDisclaimerModal
          onClose={() => setRobiDisclaimerOpen(false)}
          onContinue={handleRobiDisclaimerContinue}
        />
      )}
      {robiChatOpen && <RobiQuickAnswerModal onClose={() => setRobiChatOpen(false)} />}
      {postBuddy.disclaimerPost && (
        <AiBuddyDisclaimerModal
          onClose={() => postBuddy.setDisclaimerPost(null)}
          onContinue={() => { postBuddy.handleDisclaimerContinue().catch(() => undefined); }}
        />
      )}
      {postBuddy.robiInfoOpen && (
        <RobiInfoModal onClose={() => postBuddy.setRobiInfoOpen(false)} />
      )}
      {blockTargetPost && (
        <BlockUserModal
          username={blockTargetPost.username}
          avatarUrl={typeof blockTargetPost.avatar === 'string' ? blockTargetPost.avatar : null}
          seed={blockTargetPost.profileUserGuid ?? blockTargetPost.userId}
          isBlocked={blockTargetPost.isBlocked}
          loading={blockLoading}
          onClose={() => setBlockTargetPost(null)}
          onConfirm={() => { handleConfirmBlock().catch(() => undefined); }}
        />
      )}
    </section>
  );
}
